import styles from './Login.module.scss';

import { Button, Loader, Modal } from '@/components';
import { useNavigation } from '@/hooks/useNavigation';
import { PAGE } from '@/modules/page/routes';
import { AlgoWalletsList } from '@/modules/wallet/algo/AlgoWalletsList';
import { useStore } from '@/store';
import { activateAuthTokenByAlgorand } from '@/utils/api';

import { useWallet } from '@txnlab/use-wallet-react';
import { useEffect, useState } from 'react';

export const AlgoAuthButton = ({
  children,
  afterLoginCallback,
}: {
  children: React.ReactNode;
  afterLoginCallback?: () => void;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [isConnectingWallet, setIsConnectingWallet] = useState(false);
  const [isSigningTransaction, setIsSigningTransaction] = useState(false);
  const [abortController, setAbortController] =
    useState<AbortController | null>(null);
  const { accountStore } = useStore();
  const { activeWallet, activeAccount, signTransactions, signData } =
    useWallet();
  const { currentPage, goTo } = useNavigation();

  // Watch for wallet connection and auto-login
  useEffect(() => {
    const attemptLogin = async () => {
      if (activeWallet && activeAccount) {
        setShowWalletModal(false);
        setIsConnectingWallet(false);
        // Small delay to ensure wallet is fully connected
        setTimeout(() => algoLogin(), 100);
      }
    };

    attemptLogin();
  }, [activeWallet, activeAccount]);

  const handleButtonClick = async () => {
    // If no wallet connected, show wallet selection modal
    if (!activeAccount) {
      setShowWalletModal(true);
      setIsConnectingWallet(true);

      // Reset connecting state after 10 seconds regardless of outcome
      setTimeout(() => {
        setIsConnectingWallet(false);
        setShowWalletModal(false);
      }, 10000);

      return;
    }

    // If wallet connected, proceed with authentication
    await algoLogin();
  };

  const handleCancelConnection = () => {
    // Abort any ongoing transaction signing
    if (abortController) {
      abortController.abort();
      activeWallet?.disconnect();
    }

    setShowWalletModal(false);
    setIsConnectingWallet(false);
    setIsSigningTransaction(false);
    setIsLoading(false);
    setAbortController(null);
  };

  const algoLogin = async () => {
    setIsLoading(true);
    setIsSigningTransaction(true);

    // Create abort controller for cancellation
    const controller = new AbortController();
    setAbortController(controller);

    try {
      if (!activeAccount) {
        throw new Error('No Algorand wallet connected');
      }

      const algorandAddress = activeAccount.address;

      if (!activeWallet) {
        throw new Error('No wallet provider connected');
      }

      // Use the shared authentication function with abort signal
      await activateAuthTokenByAlgorand(
        algorandAddress,
        accountStore,
        {
          signTransactions,
          signData,
        },
        {
          abortSignal: controller.signal,
        },
      );

      // Get account data
      await accountStore.getAccountData();

      if (currentPage === PAGE.PROFILE_ID) {
        setTimeout(() => {
          goTo(PAGE.PROFILE_ID, { id: accountStore.accountData._id || 'me' });
        }, 100);
      }
      if (afterLoginCallback) afterLoginCallback();
      setTimeout(() => {
        setIsLoading(false);
      }, 300);
    } catch (error) {
      console.error('Algorand authentication failed:', error);

      if (error instanceof Error) {
        if (
          error.message.includes('User rejected') ||
          error.message.includes('cancelled') ||
          error.message.includes('USER_CANCELLED') ||
          error.message.includes('TIMEOUT')
        ) {
          alert('Transaction signing was cancelled');
        } else {
          alert(`Authentication failed: ${error.message}`);
        }
      } else {
        alert('Authentication failed: Unknown error');
      }
    } finally {
      setIsLoading(false);
      setIsSigningTransaction(false);
      setAbortController(null);
    }
  };

  return (
    <>
      <div className={styles.buttonContainer}>
        <Button
          className={styles.button}
          onClick={handleButtonClick}
          disabled={isLoading || isSigningTransaction}
        >
          {isLoading || isSigningTransaction ? (
            <Loader inButton />
          ) : !activeAccount ? (
            'Connect Algorand Wallet'
          ) : (
            children
          )}
        </Button>
        {isConnectingWallet ||
          (isSigningTransaction && (
            <button
              onClick={handleCancelConnection}
              className={styles.cancelButton}
              title={
                isSigningTransaction
                  ? 'Cancel transaction signing'
                  : 'Cancel wallet connection'
              }
            >
              ×
            </button>
          ))}
      </div>

      <Modal
        isVisible={showWalletModal}
        onClose={() => setShowWalletModal(false)}
        minHeight="auto"
      >
        <AlgoWalletsList />
      </Modal>
    </>
  );
};
